import React from 'react';
import { ChevronDown, ChevronUp, Calendar, AlertTriangle, Pin, GraduationCap } from 'lucide-react';
import SubWorkflowItem from './SubWorkflowItem';
import { format, isPast, parseISO } from 'date-fns';

/**
 * ParentWorkflowCard
 *
 * Level 1: Complete Workflow Display
 * Shows workflow header with progress and contains sub-workflows
 */
const ParentWorkflowCard = ({
  workflow,
  workflowNumber,
  isExpanded,
  onToggle,
  onTaskUpdate,
  onSubWorkflowComplete,
  navigate,
  isPinned,
  onTogglePin
}) => {
  // Determine if workflow has overdue tasks
  const hasOverdue = workflow.subWorkflows.some(sw =>
    sw.steps.some(step => {
      const dueDate = step.due_date || step.scheduled_date;
      return dueDate && isPast(parseISO(dueDate)) && step.status !== 'completed';
    })
  );

  // Determine action button state
  const getActionButton = () => {
    if (workflow.state === 'complete') {
      return { text: 'Completed', disabled: true, variant: 'completed' };
    }
    if (workflow.state === 'not_started') {
      return { text: 'Start', disabled: false, variant: 'start' };
    }
    return { text: 'Continue', disabled: false, variant: 'continue' };
  };

  const actionButton = getActionButton();

  // Badge for deadline or status
  const getBadges = () => {
    const badges = [];

    // College badge for college-specific workflows
    if (workflow.collegeName) {
      badges.push({
        type: 'college',
        text: workflow.collegeName,
        style: { background: '#f1fcf9', color: '#1a6b65', border: '1px solid #a3ecdf' }
      });
    }

    // Category badge
    if (workflow.category) {
      badges.push({
        type: 'category',
        text: workflow.category.replace('_', ' '),
        style: { background: '#f5f5f4', color: '#78716c', border: '1px solid #e7e5e4' }
      });
    }

    // Overdue badge
    if (hasOverdue) {
      badges.push({
        type: 'overdue',
        text: 'Has overdue tasks',
        style: { background: '#fff7f4', color: '#7b1348', border: '1px solid #ff6b6b' }
      });
    }

    return badges;
  };

  const badges = getBadges();

  // Check if workflow is completed
  const isCompleted = workflow.state === 'complete';

  // Check if this is a college workflow (Level 1 - College Application)
  const isCollegeWorkflow = workflow.workflowKey?.startsWith('complete_') && workflow.workflowKey?.endsWith('_app');

  // Detect mobile - use state and listener for responsive updates
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      style={{
        // Level 1 (College): 2px slate-900 border | Others: 2px teal/gray
        border: isCollegeWorkflow
          ? '2px solid #0f172a'
          : `2px solid ${isExpanded ? '#26a69a' : isCompleted ? '#d4d4d8' : '#e7e5e4'}`,
        borderRadius: isCompleted && !isExpanded ? '12px' : '16px',
        borderBottomColor: isCollegeWorkflow ? '#14b8a6' : undefined,
        borderBottomWidth: isCollegeWorkflow ? '4px' : '2px',
        marginBottom: isCompleted && !isExpanded ? '0.75rem' : (isMobile ? '1rem' : '2rem'),
        background: 'white',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        boxShadow: isExpanded && isCollegeWorkflow ? '0 8px 24px rgba(15, 23, 42, 0.15)' : isExpanded ? '0 4px 12px rgba(38, 166, 154, 0.1)' : 'none',
        opacity: isCompleted && !isExpanded ? 0.7 : 1
      }}
      onMouseEnter={(e) => {
        if (!isExpanded) {
          e.currentTarget.style.borderColor = '#26a69a';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(38, 166, 154, 0.1)';
          if (isCompleted) {
            e.currentTarget.style.opacity = 1;
          }
        }
      }}
      onMouseLeave={(e) => {
        if (!isExpanded) {
          e.currentTarget.style.borderColor = isCompleted ? '#d4d4d8' : '#e7e5e4';
          e.currentTarget.style.boxShadow = 'none';
          if (isCompleted) {
            e.currentTarget.style.opacity = 0.7;
          }
        }
      }}
    >
      {/* Workflow Header */}
      <div
        onClick={onToggle}
        style={{
          // Level 1 (College): slate-900 background | Others: white background
          background: isCollegeWorkflow ? '#0f172a' : 'transparent',
          padding: isMobile
            ? (isCollegeWorkflow ? '1rem' : (isCompleted && !isExpanded ? '0.625rem 0.75rem' : '1rem'))
            : (isCollegeWorkflow ? '1.5rem' : (isCompleted && !isExpanded ? '0.75rem 1rem' : '1.5rem')),
          cursor: 'pointer',
          display: 'flex',
          flexDirection: isMobile && isCollegeWorkflow ? 'column' : 'row',
          gap: isMobile ? '0.75rem' : (isCollegeWorkflow ? '1.25rem' : (isCompleted && !isExpanded ? '0.75rem' : '1rem')),
          alignItems: isMobile && isCollegeWorkflow ? 'stretch' : 'center',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (isCollegeWorkflow) {
            e.currentTarget.style.background = '#1e293b';
          }
        }}
        onMouseLeave={(e) => {
          if (isCollegeWorkflow) {
            e.currentTarget.style.background = '#0f172a';
          }
        }}
      >
        {/* Mobile: Top row with icon, title, and expand */}
        {isMobile && isCollegeWorkflow ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Icon */}
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: '#14b8a6',
              border: '1px solid white',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <GraduationCap size={20} />
            </div>

            {/* Title */}
            <div style={{
              flex: 1,
              fontSize: '.875rem',
              fontWeight: 500,
              color: 'white'
            }}>
              {workflow.title}
            </div>

            {/* Expand Icon */}
            <div style={{ color: 'white', flexShrink: 0 }}>
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        ) : (
          /* Desktop: Original Icon */
          <div style={{
            // Level 1 (College): 48x48 teal square with icon | Others: 40px circle with number
            width: isCollegeWorkflow ? '48px' : (isCompleted && !isExpanded ? '32px' : '40px'),
            height: isCollegeWorkflow ? '48px' : (isCompleted && !isExpanded ? '32px' : '40px'),
            borderRadius: isCollegeWorkflow ? '12px' : '50%',
            background: isCollegeWorkflow ? '#14b8a6' : (isCompleted ? '#d4d4d8' : '#26a69a'),
            border: isCollegeWorkflow ? '1px solid white' : 'none',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 500,
            fontSize: isCompleted && !isExpanded ? '0.75rem' : '.875rem',
            flexShrink: 0,
            transition: 'all 0.2s ease'
          }}>
            {isCollegeWorkflow ? <GraduationCap size={24} /> : (workflow.icon || workflowNumber)}
          </div>
        )}

        {/* Workflow Main Content */}
        <div style={{ flex: 1 }}>
          {/* Title Row */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: isCollegeWorkflow ? '0.25rem' : (isCompleted && !isExpanded ? '0' : '0.5rem')
          }}>
            <div style={{
              fontSize: isCollegeWorkflow ? '1rem' : (isCompleted && !isExpanded ? '0.875rem' : '1rem'),
              fontWeight: isCollegeWorkflow ? 600 : 600,
              color: isCollegeWorkflow ? 'white' : (isCompleted && !isExpanded ? '#71717a' : '#0f172a'),
              transition: 'all 0.2s ease'
            }}>
              {workflow.title}
            </div>
           
            {/* Show completion badge when collapsed and completed */}
            {isCompleted && !isExpanded && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.375rem 0.75rem',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 600,
                background: '#e7e5e4',
                color: '#78716c',
                border: '1px solid #d4d4d8'
              }}>
                Completed
              </span>
            )}
          </div>

          {/* Badges/Stats - Different for college workflows */}
          {isCollegeWorkflow ? (
            // Level 1 (College): Show workflow stats in white text
            // Mobile: Hide on mobile when in header (shown in separate row below)
            !isMobile && (
              <div style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>
                {workflow.subWorkflows?.length || 0} workflows · {workflow.finishedSteps} complete · {workflow.totalSteps - workflow.finishedSteps - (workflow.status === 'in_progress' ? 1 : 0)} not started
                {workflow.status === 'in_progress' && ' · 1 in progress'}
              </div>
            )
          ) : (
            // Level 2+: Show badges
            (!isCompleted || isExpanded) && (
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: '0.75rem'
              }}>
                {badges.map((badge, idx) => (
                  <span
                    key={idx}
                    style={{
                      ...badge.style,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    {badge.type === 'overdue' && <AlertTriangle size={12} />}
                    {badge.text}
                  </span>
                ))}
              </div>
            )
          )}
        </div>

        {/* Mobile: Stats and Progress Row for College Workflows */}
        {isMobile && isCollegeWorkflow && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
            {/* Stats */}
            <div style={{ fontSize: '0.75rem', color: '#cbd5e1', flex: 1 }}>
              {workflow.subWorkflows?.length || 0} workflows · {workflow.finishedSteps}/{workflow.totalSteps}
            </div>
            {/* Progress Percentage */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: 'white' }}>
                {workflow.completionPercentage}%
              </div>
            </div>
          </div>
        )}

        {/* Desktop: Progress/Expand Section - Right side */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: isCollegeWorkflow ? '1rem' : '0.5rem' }}>
            {/* Progress Percentage - For college workflows */}
            {isCollegeWorkflow && (
              <div style={{ textAlign: 'right', marginRight: '1rem' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', marginBottom: '0.125rem' }}>
                  {workflow.completionPercentage}%
                </div>
                <div style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>
                  Overall progress
                </div>
              </div>
            )}

          {/* Progress Section - Only show for non-college when expanded or not completed */}
          {!isCollegeWorkflow && (!isCompleted || isExpanded) && (
            <div style={{ marginTop: '0.75rem', flex: 1 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#26a69a' }}>
                  {workflow.finishedSteps} of {workflow.totalSteps} steps finished
                </span>
              </div>
              <div style={{
                height: '8px',
                background: '#f5f5f4',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #26a69a 0%, #1a6b65 100%)',
                  width: `${workflow.completionPercentage}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}

          {/* Desktop: Pin and Expand Icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* Pin Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin && onTogglePin(workflow.workflowKey);
              }}
              style={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: isPinned ? 'rgba(38, 166, 154, 0.1)' : 'transparent',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                color: isPinned ? '#26a69a' : isCollegeWorkflow ? 'rgba(255, 255, 255, 0.6)' : '#94a3b8',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isPinned ? 'rgba(38, 166, 154, 0.2)' : 'rgba(148, 163, 184, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isPinned ? 'rgba(38, 166, 154, 0.1)' : 'transparent';
              }}
              title={isPinned ? 'Unpin workflow' : 'Pin workflow to top'}
            >
              <Pin size={16} fill={isPinned ? '#26a69a' : 'none'} />
            </button>

            {/* Expand Icon */}
            <div style={{
              width: isCollegeWorkflow ? '24px' : '24px',
              height: isCollegeWorkflow ? '24px' : '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s ease',
              color: isCollegeWorkflow ? 'white' : '#64748b',
              flexShrink: 0
            }}>
              {isExpanded ? <ChevronUp size={isCollegeWorkflow ? 24 : 16} /> : <ChevronDown size={isCollegeWorkflow ? 24 : 16} />}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Sub-Workflows Container */}
      <div style={{
        maxHeight: isExpanded ? '4000px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease',
        background: isCollegeWorkflow ? 'rgba(250, 250, 249, 0.5)' : '#fafaf9',
        borderTop: isExpanded ? (isCollegeWorkflow ? 'none' : '2px solid #f5f5f4') : 'none'
      }}>
        <div style={{ padding: isCollegeWorkflow ? '1.5rem' : '1.5rem' }}>
          {workflow.subWorkflows.map((subWorkflow, index) => (
            <SubWorkflowItem
              key={subWorkflow.subWorkflowKey}
              subWorkflow={subWorkflow}
              workflowKey={workflow.workflowKey}
              onTaskUpdate={onTaskUpdate}
              onSubWorkflowComplete={onSubWorkflowComplete}
              navigate={navigate}
            />
          ))}
        </div>
      </div>

      {/* Additional responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          /* Ensure touch targets are at least 44px for mobile accessibility */
          button {
            min-height: 44px;
            min-width: 44px;
          }
        }
      `}</style>
    </div>
  );
};

export default ParentWorkflowCard;
